'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import useAuth from '../../hooks/useAuth'
import { sanitizeInternalRedirect } from '../../utils/safeRedirect'
import LoginModal from '../dashboard/LoginModal'

/**
 * Sign-in on the marketing homepage, which stays a modal rather than a page: the app's
 * 401 handling and the proxy both send browsers to `/?login=true`, sometimes with a
 * `next=` to return to.
 *
 * A visitor with `?login=true` gets the modal, and a sanitised `next` is stored for the
 * modal to follow after sign-in. Someone already signed in is sent straight on to `next`,
 * or to HomeHub. Rendered inside `<Suspense>` by the page because `useSearchParams` is
 * read here.
 */
export function LoginLauncher() {
  const router = useRouter()
  const params = useSearchParams()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)

  const login = params?.get('login') === 'true'
  const next = params?.get('next') ?? null

  useEffect(() => {
    if (!login) return
    const nextUrl = sanitizeInternalRedirect(next, window.location.origin)

    if (user) {
      router.replace(nextUrl || '/homehub')
      return
    }

    if (nextUrl) {
      localStorage.setItem('loginRedirectUrl', nextUrl)
    } else {
      localStorage.removeItem('loginRedirectUrl')
    }
    setOpen(true)
  }, [login, next, user, router])

  const close = () => {
    setOpen(false)
    // Drop the query so a reload does not reopen the modal.
    if (login) router.replace('/')
  }

  return <LoginModal open={open} onClose={close} />
}
